<template>
  <div
    ref="player"
    :style="{
      transform: `scale(${scaledSize})`,
      width: `${scaledWidth}%`
    }"
    :class="{'simplecast-player': true, 'standard-size': !sizeMini}"
  >
    <Main :store="store" />

    <transition name="fade">
      <SharePanel
        v-if="showShare && !sizeMini"
        :store="store"
      />
      <SubscribePanel
        v-if="showSubscribe && !sizeMini"
        :store="store"
      />
      <ShareSubscribePanelMini
        v-if="showSubscribe && sizeMini"
        :store="store"
      />
    </transition>
  </div>
</template>

<script>
import Main from './PlayerMain'
import Store from '../store'
import SharePanel from './SharePanel'
import SubscribePanel from './SubscribePanel'
import ShareSubscribePanelMini from './ShareSubscribePanelMini'

export default {
  components: {
    Main,
    ShareSubscribePanelMini,
    SharePanel,
    SubscribePanel
  },
  props: {
    adFreeAudio: {
      type: String,
      default: ''
    },
    cacheBust: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: ''
    },
    dark: {
      type: Boolean,
      default: false
    },
    episode: {
      type: Object,
      default: () => {}
    },
    width: {
      type: Number,
      default: 444 // set to default oembed width
    },
    height: {
      type: Number,
      default: 200
    },
    hideShare: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: ''
    },
    podcast: {
      type: Object,
      default: () => {}
    },
    timejump: {
      type: Number,
      default: 0
    },
    websocket: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      store: new Store()
    }
  },
  computed: {
    showShare () { return this.store.state.showShare },
    showSubscribe () { return this.store.state.showSubscribe },
    sizeMini () { return this.store.state.size === 'size-mini' },
    scaledSize () {
      if (!this.sizeMini && this.height < 200) {
        return this.height / 200
      }
      return 1
    },
    scaledWidth () {
      if (!this.sizeMini && this.height < 200) {
        const smallWidth = this.width * this.scaledSize
        return this.width / smallWidth * 100
      }
      return 100
    }
  },
  watch: {
    color (color) {
      this.store.setColor(color)
    },
    height (height) {
      this.store.setSize(height)
    },
    episode (val) {
      this.store.setEpisode(val)
      this.store.getEpisodeWaveform(val)
    },
    podcast (val) {
      this.store.setPodcast(val)
    }
  },
  created () {
    this.store.setAdFreeAudio(this.adFreeAudio)
    this.store.setCacheBust(this.cacheBust)
    this.store.setIsDark(this.dark)
    this.store.setHideShareSubscribeButtons(this.hideShare)
    this.store.setColor(this.color)
    this.store.setCurrentTime(this.timejump)
    this.store.setSize(this.height)
    this.store.setWebsocketPath(this.websocket)

    if (this.id !== '') {
      this.store.getEpisode(this.id)
    } else if (this.episode && this.podcast) {
      this.store.setEpisode(this.episode)
      this.store.setPodcast(this.podcast)
      this.store.getEpisodeWaveform(this.episode)

      if (this.podcast.is_private !== true) {
        this.store.getDistributionChannels(this.podcast.distribution_channels.href)
        this.store.getSeasons(this.podcast.seasons.href)
      }
    } else {
      this.store.setIsLoaded(true)
      console.error('Either an episode ID needs to be set, or both an episode and podcast Object. You have not set either (╯°□°)╯︵ ┻━┻')
    }
  },
  mounted () {
    window.addEventListener('resize', this.resize)
    this.resize()
  },
  methods: {
    resize () {
      window.parent.postMessage(JSON.stringify({
        src: window.location.toString(),
        context: 'iframe.resize',
        height: 200
      }), '*')
    }
  }
}
</script>

<style lang="scss">
@import 'node_modules/simplecast-styles/src/assets/styles/_components/ratios.scss';
@import 'node_modules/simplecast-styles/src/assets/styles/_core/colors.scss';
@import 'node_modules/simplecast-styles/src/assets/styles/_core/faces.scss';

/* NOTE
html is set to 87.5% so that all the REM measurements throughout Skeleton
are based on 14px sizing. So basically 1rem = 14px :) */
html, body {
  color: $black-800;
  font-size: 87.5%;

  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%;
}

</style>

<style lang="scss" scoped>
@import 'node_modules/simplecast-styles/src/assets/styles/_core/colors.scss';

.simplecast-player {
  height: 100%;
  max-height: 200px;
  position: relative;
  &.standard-size {
    transform-origin: top left;
    height: 200px;
  }
  /deep/ {
    button,
    input,
    textarea,
    select,
    a {
      outline: 0;
    }
  }
}

.simplecast-player {

 /deep/ .focusable {
   line-height: 0;
 }

   /deep/ button,
   /deep/ a,
   /deep/ .focusable {
    position: relative;
    display: inline-flex;
    justify-content: center;
    align-items: center;

   }
  /deep/ button:focus,
  /deep/ a:focus,
  /deep/ .focusable:focus-within {
      $focus-margin: -4px;
      // this is length of a dash
      $dash-length: 10px;
      // this is how thick the dash is
      $dash-thickness: 2px;
      // color of focus
      $color: $black-trans-500;

      &::after {
      content: "";
      display: block;
      position: absolute;
      top: $focus-margin;
      left: $focus-margin;
      right: $focus-margin;
      bottom: $focus-margin;
      background-image: linear-gradient(to right, $color 50%, transparent 50%),
        linear-gradient(to right, $color 50%, transparent 50%),
        linear-gradient(to bottom, $color 50%, transparent 50%),
        linear-gradient(to bottom, $color 50%, transparent 50%);
      background-size: $dash-length $dash-thickness, $dash-length $dash-thickness,
        $dash-thickness $dash-length, $dash-thickness $dash-length;
      background-position: 0 0, 0 100%, 0 0, 100% 0;
      background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
      }
    }
  /deep/ .dark {
    /deep/ button:focus,
    /deep/ a:focus,
    /deep/ .focusable:focus-within {
      &::after {
        $color: $white-trans-600;
        background-image: linear-gradient(to right, $color 50%, transparent 50%),
        linear-gradient(to right, $color 50%, transparent 50%),
        linear-gradient(to bottom, $color 50%, transparent 50%),
        linear-gradient(to bottom, $color 50%, transparent 50%);
      }
    }
  }
  }
</style>

<style lang="scss">
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s !important;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0 !important;
}
</style>
